"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";

interface HeroSectionProps {
  variant: string;
  config: any;
  brand: any;
  theme: any;
}

export function HeroSection({ variant, config, brand, theme }: HeroSectionProps) {
  const { heading, subheading, ctaText, ctaLink, secondaryCtaText, secondaryCtaLink, backgroundImage, sideImage, textAlignment } = config;

  // Simple centered variant
  if (variant === "centered") {
    return (
      <section 
        className="relative py-24 md:py-32 flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: theme.primaryColor + '10' }} // Slight tint
      >
        {backgroundImage && (
          <div className="absolute inset-0 z-0">
            <Image 
              src={backgroundImage} 
              alt={heading || "Hero"} 
              fill 
              className="object-cover opacity-20" 
              priority
            />
          </div>
        )}
        
        <div className="container mx-auto px-4 z-10 text-center max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight text-gray-900">
            {heading || brand.name}
          </h1>
          {subheading && (
            <p className="text-xl md:text-2xl mb-10 text-gray-700">
              {subheading}
            </p>
          )}
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {ctaText && ctaLink && (
              <Link 
                href={ctaLink}
                className="px-8 py-4 rounded-full font-semibold text-lg transition-transform hover:scale-105"
                style={{ backgroundColor: theme.primaryColor, color: '#fff' }}
              >
                {ctaText}
              </Link>
            )}
            {secondaryCtaText && secondaryCtaLink && (
              <Link 
                href={secondaryCtaLink}
                className="px-8 py-4 rounded-full font-semibold text-lg border-2 transition-transform hover:scale-105"
                style={{ borderColor: theme.primaryColor, color: theme.primaryColor }}
              >
                {secondaryCtaText}
              </Link>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Split-image variant
  return (
    <section className="py-12 md:py-24 overflow-hidden relative" style={{ backgroundColor: theme.backgroundColor }}>
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          
          <div className={`flex-1 ${textAlignment === 'center' ? 'text-center' : textAlignment === 'right' ? 'text-right' : 'text-left'}`}>
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight text-gray-900">
              {heading || brand.name}
            </h1>
            {subheading && (
              <p className="text-lg md:text-xl mb-8 text-gray-600 max-w-2xl mx-auto lg:mx-0">
                {subheading}
              </p>
            )}
            <div className={`flex flex-col sm:flex-row gap-4 ${textAlignment === 'center' ? 'justify-center' : textAlignment === 'right' ? 'justify-end' : 'justify-start'}`}>
              {ctaText && ctaLink && (
                <Link 
                  href={ctaLink}
                  className="px-8 py-3 rounded font-medium text-lg"
                  style={{ backgroundColor: theme.primaryColor, color: '#fff' }}
                >
                  {ctaText}
                </Link>
              )}
            </div>
          </div>

          {(sideImage || brand.coverImages?.[0] || brand.logo) && (
            <div className="flex-1 w-full h-[400px] md:h-[600px] relative rounded-2xl overflow-hidden shadow-2xl">
              <Image 
                src={sideImage || brand.coverImages?.[0] || brand.logo} 
                alt="Brand hero" 
                fill 
                className="object-cover"
                priority
              />
            </div>
          )}
          
        </div>
      </div>
    </section>
  );
}
